import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Upload, Sparkles, GitBranch, Database, ArrowRight, Play, ChevronDown, CheckCircle2, FileText, Shield, Settings, Zap } from 'lucide-react';
const processSteps = [{
  id: 1,
  icon: Upload,
  title: 'Upload Transcript',
  description: 'Drop in a meeting transcript, call recording, or notes document. We support .txt, .docx, .pdf, and audio files.',
  expandedContent: {
    details: 'Our platform accepts multiple file formats including text documents, Word files, PDFs, and even audio recordings. Simply drag and drop your file or paste the content directly.',
    features: ['Support for .txt, .docx, .pdf, and .mp3/.wav files', 'Drag-and-drop interface or paste directly', 'Automatic language detection', 'Secure file processing with encryption']
  }
}, {
  id: 2,
  icon: Sparkles,
  title: 'AI Extraction',
  description: 'Our model identifies clients, projects, requirements, and feature requests—no manual tagging needed.',
  expandedContent: {
    details: 'Advanced AI analyzes your transcript to extract structured data including client names, project scopes, requirements, and feature requests with high accuracy.',
    features: ['Automatic entity recognition (clients, projects, features)', 'Context-aware requirement extraction', 'Relationship mapping between entities', '95%+ accuracy on standard transcripts']
  }
}, {
  id: 3,
  icon: GitBranch,
  title: 'Review & Refine',
  description: 'See extracted entities in an editable table. Approve, edit, or flag items before proceeding.',
  expandedContent: {
    details: 'Review all extracted data in an intuitive interface. Edit any field, merge duplicates, or flag items for further review before generating your ERP files.',
    features: ['Editable table view with inline editing', 'Bulk select and approve multiple items', 'Conflict detection and resolution', 'Version history for all changes']
  }
}, {
  id: 4,
  icon: Database,
  title: 'Generate ERP Files',
  description: 'Auto-generate import files mapped to SAP, Oracle, NetSuite, or custom templates. Download or push directly.',
  expandedContent: {
    details: 'Generate perfectly formatted import files for your ERP system. Download as Excel/CSV or push directly via API integration.',
    features: ['Pre-built templates for major ERP systems', 'Custom field mapping for any system', 'Excel/CSV export with proper formatting', 'Direct API integration available']
  }
}];
const faqs = [{
  question: 'What file formats can I upload?',
  answer: 'We support .txt, .docx, .pdf files for text documents, and .mp3, .wav files for audio recordings. You can also paste content directly into the platform.'
}, {
  question: 'How accurate is the AI extraction?',
  answer: 'Our AI achieves 95%+ accuracy on standard business transcripts. The review step allows you to verify and edit any extracted data before generating ERP files.'
}, {
  question: 'Can I customize the ERP field mappings?',
  answer: 'Yes! While we provide pre-built templates for SAP, Oracle, NetSuite, and other major systems, you can create custom field mappings for any ERP system.'
}, {
  question: 'Is my data secure?',
  answer: 'Absolutely. All files are encrypted in transit and at rest. We never share your data with third parties. Files are automatically deleted after processing.'
}, {
  question: 'What happens if the AI makes a mistake?',
  answer: 'The review step is designed for this. You can edit any extracted field, merge duplicates, or flag items. All changes are tracked with version history.'
}];
const erpLogos = [{
  name: 'SAP',
  logo: 'https://cdn.magicpatterns.com/uploads/hoiN28WVwa5Xa8J26tFdKS/sap-logo-png_seeklogo-465583.png',
  isImage: true
}, {
  name: 'Oracle NetSuite',
  logo: 'https://cdn.magicpatterns.com/uploads/9cy12JwyELAZZejDieg4zn/NetSuite-Logo-1536x864.png',
  isImage: true
}, {
  name: 'Microsoft Dynamics',
  logo: 'https://cdn.magicpatterns.com/uploads/srn3XjgE6TAcQ5ezQD99n3/Dynamics-365-logo-1536x864.png',
  isImage: true
}, {
  name: 'Odoo',
  logo: 'https://cdn.magicpatterns.com/uploads/qKwf9WcNs7k4pBuVUdTd4A/image.png',
  isImage: true
}, {
  name: 'Excel/CSV',
  color: '#217346',
  isImage: false
}, {
  name: 'Custom Documentation',
  color: '#7dd3fc',
  isImage: false
}];
export function HowItWorksPage() {
  const [expandedStep, setExpandedStep] = useState<number | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  return <div className="min-h-screen bg-white text-slate-900" data-id="element-233">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200" data-id="element-234">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-235">
          <div className="flex items-center justify-between h-16" data-id="element-236">
            <div className="flex items-center gap-8" data-id="element-237">
              <a href="/" className="flex flex-col" data-id="element-238">
                <div className="flex items-center gap-2" data-id="element-239">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/46nVjihkQgtRGHjwuLjy14/Untitled_design_%282%29.png" alt="Foundations" className="h-8 w-8" data-id="element-240" />
                  <span className="text-xl font-bold text-slate-900" data-id="element-241">
                    Foundations
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium ml-10" data-id="element-242">
                  <span data-id="element-243">Powered by</span>
                  <a href="https://www.thruline.ai" target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity" data-id="element-244">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bxVHxBAzSWANMpXq8oviFD/thruline-logo-light-trans_%281%29.png" alt="Thruline" className="h-3 w-auto" data-id="element-245" />
                  </a>
                </div>
              </a>
              <a href="/" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-id="element-246">
                Home
              </a>
              <a href="/how-it-works" className="text-sm font-medium text-primary-blue" data-id="element-247">
                How It Works
              </a>
            </div>
            <Button size="sm" onClick={() => window.open('https://calendly.com/alex-thruline/erp-client-portal', '_blank')} data-id="element-248">
              Schedule Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-id="element-249">
        <div className="absolute inset-0 bg-gradient-radial from-primary-cyan/10 via-transparent to-transparent opacity-30" data-id="element-250"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-251">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-252">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-cyan/10 border border-primary-cyan/20 mb-6" data-id="element-253">
              <span className="text-sm font-medium text-primary-blue" data-id="element-254">
                How It Works
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight text-slate-900" data-id="element-255">
              From Transcript to ERP-Ready Data in Minutes
            </h1>
            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-256">
              Upload a client conversation. Our AI extracts projects,
              requirements, and features—then maps them to your ERP templates
              automatically.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-257">
              <Button size="lg" className="bg-gradient-primary hover:bg-gradient-primary-hover text-white" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-259" />} onClick={() => window.open('https://calendly.com/alex-thruline/erp-client-portal', '_blank')} data-id="element-258">
                Try It Free
              </Button>
              <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50" leftIcon={<Play className="h-4 w-4" data-id="element-261" />} onClick={() => window.open('https://www.loom.com/share/224e54ecaf844fcc9e98db1fc3a2236a', '_blank')} data-id="element-260">
                Watch Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Timeline Section */}
      <section className="py-24 relative bg-slate-50" data-id="element-262">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-263">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative" data-id="element-264">
            {/* Connecting Line */}
            <div className="hidden lg:block absolute top-16 left-[12%] right-[12%] h-[2px] border-t-2 border-dashed border-slate-300" data-id="element-265"></div>

            {processSteps.map((step, index) => {
            const Icon = step.icon;
            const isExpanded = expandedStep === step.id;
            return <motion.div key={step.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="relative" data-id="element-266">
                  <motion.button onClick={() => setExpandedStep(isExpanded ? null : step.id)} className="w-full bg-white border border-slate-200 rounded-xl p-6 text-left hover:border-primary-cyan transition-all duration-300 cursor-pointer group shadow-sm" whileHover={{
                y: -4
              }} data-id="element-267">
                    <div className="absolute -top-3 -left-3 h-10 w-10 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold text-sm z-10 shadow-lg shadow-primary-blue/30" data-id="element-268">
                      {step.id}
                    </div>
                    <div className="h-12 w-12 bg-gradient-to-br from-primary-purple/10 to-accent/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-269">
                      <Icon className="h-6 w-6 text-primary-blue" data-id="element-270" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-slate-900" data-id="element-271">
                      {step.title}
                    </h3>
                    <p className="text-sm text-slate-600 mb-3" data-id="element-272">
                      {step.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-primary-blue" data-id="element-273">
                      <span data-id="element-274">Learn more</span>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-275" />
                    </div>
                  </motion.button>

                  <AnimatePresence data-id="element-276">
                    {isExpanded && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="mt-4 bg-white border border-primary-cyan/30 rounded-xl p-6 overflow-hidden shadow-sm" data-id="element-277">
                        <p className="text-sm text-slate-700 mb-4" data-id="element-278">
                          {step.expandedContent.details}
                        </p>
                        <ul className="space-y-2" data-id="element-279">
                          {step.expandedContent.features.map((feature, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600" data-id="element-280">
                              <CheckCircle2 className="h-4 w-4 text-primary-blue shrink-0 mt-0.5" data-id="element-281" />
                              <span data-id="element-282">{feature}</span>
                            </li>)}
                        </ul>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Interactive Demo Preview */}
      <section className="py-24 bg-white" data-id="element-283">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-284">
          <div className="text-center mb-16" data-id="element-285">
            <h2 className="text-4xl font-bold mb-4 text-slate-900" data-id="element-286">
              See It In Action
            </h2>
            <p className="text-xl text-slate-600" data-id="element-287">
              Here's what the extraction interface looks like
            </p>
          </div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-288">
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 shadow-2xl" data-id="element-289">
              {/* Mock Interface */}
              <div className="space-y-6" data-id="element-290">
                {/* Conflicting Items */}
                <div className="border border-amber-500/30 rounded-lg overflow-hidden" data-id="element-291">
                  <div className="bg-amber-500/10 px-4 py-3 border-b border-amber-500/30" data-id="element-292">
                    <h3 className="font-semibold text-amber-400 flex items-center gap-2" data-id="element-293">
                      <span className="h-2 w-2 rounded-full bg-amber-400" data-id="element-294"></span>
                      Conflicting Items (3)
                    </h3>
                  </div>
                  <div className="p-4 space-y-2" data-id="element-295">
                    {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg" data-id="element-296">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300" data-id="element-297" />
                        <div className="flex-1 grid grid-cols-3 gap-4 text-sm" data-id="element-298">
                          <span className="text-slate-700" data-id="element-299">Project Alpha</span>
                          <span className="text-slate-600" data-id="element-300">
                            Requirement #{i}
                          </span>
                          <span className="text-amber-400" data-id="element-301">Needs review</span>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* New Items */}
                <div className="border border-green-500/30 rounded-lg overflow-hidden" data-id="element-302">
                  <div className="bg-green-500/10 px-4 py-3 border-b border-green-500/30" data-id="element-303">
                    <h3 className="font-semibold text-green-400 flex items-center gap-2" data-id="element-304">
                      <span className="h-2 w-2 rounded-full bg-green-400" data-id="element-305"></span>
                      New Items (12)
                    </h3>
                  </div>
                  <div className="p-4 space-y-2" data-id="element-306">
                    {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg" data-id="element-307">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300" data-id="element-308" />
                        <div className="flex-1 grid grid-cols-3 gap-4 text-sm" data-id="element-309">
                          <span className="text-slate-700" data-id="element-310">Project Beta</span>
                          <span className="text-slate-600" data-id="element-311">Feature #{i}</span>
                          <span className="text-green-400" data-id="element-312">
                            Ready to import
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>

                {/* Delete Items */}
                <div className="border border-red-500/30 rounded-lg overflow-hidden" data-id="element-313">
                  <div className="bg-red-500/10 px-4 py-3 border-b border-red-500/30" data-id="element-314">
                    <h3 className="font-semibold text-red-400 flex items-center gap-2" data-id="element-315">
                      <span className="h-2 w-2 rounded-full bg-red-400" data-id="element-316"></span>
                      Delete Items (2)
                    </h3>
                  </div>
                  <div className="p-4 space-y-2" data-id="element-317">
                    {[1, 2].map(i => <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg" data-id="element-318">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300" data-id="element-319" />
                        <div className="flex-1 grid grid-cols-3 gap-4 text-sm" data-id="element-320">
                          <span className="text-slate-700" data-id="element-321">Old Project</span>
                          <span className="text-slate-600" data-id="element-322">
                            Deprecated #{i}
                          </span>
                          <span className="text-red-400" data-id="element-323">
                            Mark for deletion
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Annotations */}
            <div className="absolute top-20 -right-4 hidden xl:block" data-id="element-324">
              <div className="bg-white border border-primary-cyan/30 rounded-lg p-3 shadow-xl max-w-[200px]" data-id="element-325">
                <p className="text-xs text-slate-700" data-id="element-326">
                  Editable fields - click to modify any value
                </p>
              </div>
              <div className="w-px h-12 bg-primary-cyan/30 ml-4" data-id="element-327"></div>
            </div>

            <div className="absolute top-60 -left-4 hidden xl:block" data-id="element-328">
              <div className="bg-white border border-primary-cyan/30 rounded-lg p-3 shadow-xl max-w-[200px]" data-id="element-329">
                <p className="text-xs text-slate-700" data-id="element-330">
                  Bulk select - approve multiple items at once
                </p>
              </div>
              <div className="w-px h-12 bg-primary-cyan/30 ml-4" data-id="element-331"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Supported Outputs */}
      <section className="py-24 bg-slate-50" data-id="element-332">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-333">
          <div className="text-center mb-16" data-id="element-334">
            <h2 className="text-4xl font-bold mb-4 text-slate-900" data-id="element-335">
              Works with ERPs you support
            </h2>
            <p className="text-xl text-slate-600" data-id="element-336">
              Pre-loaded system documentation from the following sources
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-337">
            {erpLogos.map((erp, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} whileHover={{
            scale: 1.05
          }} className="group bg-white border border-slate-200 rounded-xl p-6 flex items-center justify-center h-24 hover:border-primary-cyan/50 hover:shadow-md transition-all cursor-pointer" data-id="element-338">
                {erp.isImage ? <img src={erp.logo} alt={erp.name} className="h-10 w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300" data-id="element-339" /> : <span className="font-bold text-center text-slate-400 group-hover:text-[var(--color)] transition-colors duration-300" style={{
              '--color': erp.color
            } as React.CSSProperties} data-id="element-340">
                    {erp.name}
                  </span>}
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-24 bg-white" data-id="element-341">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-342">
          <div className="text-center mb-16" data-id="element-343">
            <h2 className="text-4xl font-bold mb-4 text-slate-900" data-id="element-344">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600" data-id="element-345">
              Everything you need to know
            </p>
          </div>

          <div className="space-y-4" data-id="element-346">
            {faqs.map((faq, index) => {
            const isExpanded = expandedFaq === index;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-347">
                  <button onClick={() => setExpandedFaq(isExpanded ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors" data-id="element-348">
                    <span className="font-semibold text-lg text-slate-900" data-id="element-349">
                      {faq.question}
                    </span>
                    <ChevronDown className={`h-5 w-5 text-primary-blue transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-350" />
                  </button>

                  <AnimatePresence data-id="element-351">
                    {isExpanded && <motion.div initial={{
                  height: 0
                }} animate={{
                  height: 'auto'
                }} exit={{
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-352">
                        <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-353">
                          {faq.answer}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-24 bg-slate-50" data-id="element-354">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-355">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-gradient-to-br from-primary-purple/10 to-accent/10 border border-primary-cyan/30 rounded-2xl p-12 text-center shadow-lg" data-id="element-356">
            <h2 className="text-4xl font-bold mb-4 text-slate-900" data-id="element-357">
              Ready to streamline your implementations?
            </h2>
            <p className="text-xl text-slate-600 mb-8" data-id="element-358">
              Start extracting insights from your first transcript today.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6" data-id="element-359">
              <Button size="lg" className="bg-gradient-primary hover:bg-gradient-primary-hover text-white" rightIcon={<ArrowRight className="h-4 w-4" data-id="element-361" />} onClick={() => window.open('https://calendly.com/alex-thruline/erp-client-portal', '_blank')} data-id="element-360">
                Start Free Trial
              </Button>
              <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-white" onClick={() => window.open('https://calendly.com/alex-thruline/erp-client-portal', '_blank')} data-id="element-362">
                Book a Demo
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Overlay */}
      <AnimatePresence data-id="element-363">
        {showVideo && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowVideo(false)} data-id="element-364">
            <motion.div initial={{
          scale: 0.9
        }} animate={{
          scale: 1
        }} exit={{
          scale: 0.9
        }} className="bg-white border border-slate-200 rounded-2xl p-8 max-w-4xl w-full" onClick={e => e.stopPropagation()} data-id="element-365">
              <div className="flex items-center justify-between mb-6" data-id="element-366">
                <h3 className="text-2xl font-bold text-slate-900" data-id="element-367">
                  Product Demo
                </h3>
                <button onClick={() => setShowVideo(false)} className="text-slate-600 hover:text-slate-900 transition-colors" data-id="element-368">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-369">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-370" />
                  </svg>
                </button>
              </div>

              <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center" data-id="element-371">
                <div className="text-center" data-id="element-372">
                  <Play className="h-16 w-16 text-primary-blue mx-auto mb-4" data-id="element-373" />
                  <p className="text-slate-600" data-id="element-374">Video will be embedded here</p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}